import React, { useState, useMemo, useEffect } from "react";
import { FaTasks, FaListUl } from "react-icons/fa";
import { useT } from "../../contexts/TranslationContext";
import { displayText } from "../../utils/translationDisplay";

/**
 * Format hours display for tasks (e.g., "25.5 / 40h" or "25.5h")
 */
const formatHoursDisplay = (tracked, estimated) => {
  const trackedNum = tracked || 0;
  const hasEstimate = estimated !== undefined && estimated !== null && estimated > 0;
  if (!hasEstimate && trackedNum === 0) return null;
  if (hasEstimate) {
    return `${trackedNum.toFixed(1)} / ${estimated.toFixed(1)}h`;
  }
  return `${trackedNum.toFixed(1)}h`;
};

/**
 * Read-only view of a task bundle: same structure as TaskPickerModal "All tasks" tab.
 * Task set header with expand/collapse, tasks and subtasks with hours and billing.
 *
 * @param {object} bundle - Task bundle from /task-bundles/projects/:projectId/task-bundle
 * @param {boolean} loading - Show loading state
 */
function AllTasksTreeView({ bundle, loading }) {
  const { t } = useT();
  const [expandedTasks, setExpandedTasks] = useState({});
  const [expandedTaskSets, setExpandedTaskSets] = useState({});

  const groupedTasks = useMemo(() => {
    if (!bundle?.tasks) return [];
    return bundle.tasks.map((task, taskIdx) => ({
      ...task,
      taskIdx,
      bundleId: bundle._id,
    }));
  }, [bundle]);

  // Project page: task set open, subtasks collapsed by default
  useEffect(() => {
    if (!bundle) return;
    if (bundle._id) {
      setExpandedTaskSets((prev) => ({ ...prev, [bundle._id]: true }));
    }
    // Do not expand tasks with subtasks by default (subtasks stay collapsed)
  }, [bundle]);

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const toggleTaskSetExpand = (bundleId) => {
    setExpandedTaskSets((prev) => ({
      ...prev,
      [bundleId]: !prev[bundleId],
    }));
  };

  if (loading) {
    return (
      <div>
        <p className="text-gray-500">Loading tasks…</p>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div>
        <p className="text-gray-600">No tasks have been added to this project yet.</p>
      </div>
    );
  }

  if (groupedTasks.length === 0) {
    return (
      <div>
        <p className="text-center text-gray-500 py-8">{t("taskPicker.noTasksAvailable")}</p>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Task Set Header */}
      <button
        type="button"
        onClick={() => toggleTaskSetExpand(bundle._id)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 text-left"
      >
        <div className="flex items-center gap-2">
          <FaTasks className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <span className="font-semibold text-gray-900">
            {bundle.taskSetRef?.name || t("taskPicker.otherTasks")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 flex items-center gap-1.5">
            <FaTasks className="w-4 h-4 flex-shrink-0" aria-hidden />
            <span>{groupedTasks.length}</span>
            <span className="hidden md:inline">{t("taskPicker.tasksLabel")}</span>
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${
              expandedTaskSets[bundle._id] ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Task Set Content */}
      {expandedTaskSets[bundle._id] && (
        <div className="divide-y divide-gray-100">
          {groupedTasks.map((task) => (
            <div key={task._id}>
              {task.subtasks && task.subtasks.length > 0 ? (
                <>
                  <div className="flex items-center p-4 hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {displayText(task, "title")}
                        </span>
                        {task.billingModel && (
                          <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                            {task.billingModel === "hourly"
                              ? t("taskPicker.billingHourly")
                              : task.billingModel === "fixed_price"
                                ? t("taskPicker.billingFixedPrice")
                                : ""}
                          </span>
                        )}
                        {formatHoursDisplay(task.trackedHours, task.estimatedHours) && (
                          <span
                            className={`text-xs ${
                              task.estimatedHours &&
                              (task.trackedHours || 0) > task.estimatedHours
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {formatHoursDisplay(task.trackedHours, task.estimatedHours)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleTaskExpand(task._id)}
                      className="flex items-center gap-2 ml-2 text-gray-500 hover:text-gray-700"
                    >
                      <FaListUl className="w-4 h-4 flex-shrink-0" aria-hidden />
                      <span className="text-sm">{task.subtasks.length}</span>
                      <span className="hidden md:inline text-sm">{t("taskPicker.subtasksLabel")}</span>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                          expandedTasks[task._id] ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                  {expandedTasks[task._id] && (
                    <div className="pl-4 border-l-2 border-gray-200 ml-4 pb-2">
                      {task.subtasks.map((subtask, subtaskIdx) => {
                        const subtaskBillingModel =
                          subtask.billingModel || task.billingModel;
                        return (
                          <div
                            key={subtask._id}
                            className="flex items-center gap-1 p-3 rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex-1 min-w-0 flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-gray-700">
                                    {displayText(subtask, "title")}
                                  </span>
                                  {subtaskBillingModel && (
                                    <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                      {subtaskBillingModel === "hourly"
                                        ? t("taskPicker.billingHourly")
                                        : subtaskBillingModel === "fixed_price"
                                          ? t("taskPicker.billingFixedPrice")
                                          : ""}
                                    </span>
                                  )}
                                  {formatHoursDisplay(
                                    subtask.trackedHours,
                                    subtask.estimatedHours
                                  ) && (
                                    <span
                                      className={`text-xs ${
                                        subtask.estimatedHours &&
                                        (subtask.trackedHours || 0) >
                                          subtask.estimatedHours
                                          ? "text-red-600"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      {formatHoursDisplay(
                                        subtask.trackedHours,
                                        subtask.estimatedHours
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center p-4 hover:bg-gray-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {displayText(task, "title")}
                      </span>
                      {task.billingModel && (
                        <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                          {task.billingModel === "hourly"
                            ? t("taskPicker.billingHourly")
                            : task.billingModel === "fixed_price"
                              ? t("taskPicker.billingFixedPrice")
                              : ""}
                        </span>
                      )}
                      {formatHoursDisplay(task.trackedHours, task.estimatedHours) && (
                        <span
                          className={`text-xs ${
                            task.estimatedHours &&
                            (task.trackedHours || 0) > task.estimatedHours
                              ? "text-red-600"
                              : "text-gray-500"
                          }`}
                        >
                          {formatHoursDisplay(task.trackedHours, task.estimatedHours)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AllTasksTreeView;
